import React, {Component} from 'react';
import 'react-day-picker/lib/style.css';
import 'react-table/react-table.css';

import DateRangePicker from 'components/DateRangePicker';
import ReportsSelect from 'components/ReportsSelect';
import ReportsTable from 'components/ReportsTable';
import ReportsTips from 'components/ReportsTips';
import {WORKERS as USER} from 'constants/accounts';
import api from 'utils/api';
import {gettext} from 'utils/text';

/**
 *  @param {Object} product - A product that is in production.
 *  @param {*} product.id - Id of the product.
 *  @param {*} product.welder - Welder assigned to the product.
 *  @param {*} product.article_code - Article code of the product from Profit API data.
 *  @param {*} product.order - Sales order the product originates from.
 *  @param {*} product.latest_welder_qcf - The latest quality control feedback for welding.
 *  @param {*} product.earliest_work_log_time - Time when the first work log was started.
 *  @param {*} product.estimated_vs_actual_label - Chosen estimated vs actual time label by quality control worker.
 *  @param {*} product.tolerance_label - Tolerance label (ok or not ok).
 *  @param {*} product.welding_quality_label - Welding quality label (good, neutral or bad).
 *  @param {*} product.latest_welder_quality_control_feedback - Latest QA feedback to welding.
 */


class Reports extends Component {
    state = {
        selectedStartDay: null,
        selectedEndDay: null,
        selectedWelder: null,
        selectedProduct: null,
        tableRows: [],
        isLoading: false,
        latestTimestamp: null,
        noDataText: gettext('Select a date range, a welder or a product to see results'),
    };

    setStartDay = (day) => {
        this.setState({selectedStartDay: day}, this.fetchTableData);
    };

    setEndDay = (day) => {
        this.setState({selectedEndDay: day}, this.fetchTableData);
    };

    setSelectedWelder = (selectedWelder) => {
        this.setState({selectedWelder}, this.fetchTableData);
    };

    setSelectedProduct = (selectedProduct) => {
        this.setState({selectedProduct}, this.fetchTableData);
    };

    fetchTableData = () => {
        const timestamp = new Date().getTime();
        this.setState({latestTimestamp: timestamp});

        const queryParams = {
            welder: this.state.selectedWelder ? this.state.selectedWelder.value : null,
            productCode: this.state.selectedProduct ? this.state.selectedProduct.label : null,
            startDate: this.state.selectedStartDay ? this.state.selectedStartDay.toISOString() : null,
            endDate: this.state.selectedEndDay ? this.state.selectedEndDay.toISOString() : null,
            queryTimestamp: timestamp,
        };
        if (queryParams.startDate === null && queryParams.endDate === null
            && queryParams.welder === null && queryParams.productCode === null) {
            this.setState({
                noDataText: gettext('Select a date range, a welder or a product to see results'),
                tableRows: [],
            });
            return;
        }

        const noRowsFoundMessage = gettext('No rows found');
        if (this.state.noDataText !== noRowsFoundMessage) {
            this.setState({noDataText: noRowsFoundMessage});
        }

        this.setState({isLoading: true});

        api.productionReportsProductList.fetch(null, queryParams).then(
            (data) => {
                if (data.timestamp !== this.state.latestTimestamp.toString()) {
                    // The response was outdated, ignore the result
                    return;
                }
                this.setState({
                    tableRows: data.rows.reduce((result, product) => {
                        const latest = product.latest_welder_quality_control_feedback;
                        const hasLatest = latest && latest.created !== null;
                        let totalPercentage = null;

                        if (hasLatest) {
                            totalPercentage = (
                                latest.estimated_vs_actual_salary_percentage +
                                latest.tolerance_salary_percentage +
                                latest.welding_quality_salary_percentage
                            );
                        }

                        // There is no earliest work log time if a product
                        // is in production and no welding has been done
                        if (product.earliest_work_log_time !== null) {
                            result.push({
                                productId: product.id,
                                welderId: product.welder.id,
                                created: new Date(product.earliest_work_log_time),
                                welder: product.welder.name,
                                orderNumber: parseInt(product.order.full_number, 10),
                                product: product.article_code,
                                estimatedTime: product.estimated_duration
                                    ? parseFloat(product.estimated_duration.toFixed(1))
                                    : 0.0,
                                actualTime: product.actual_welding_time
                                    ? parseFloat(product.actual_welding_time.toFixed(1))
                                    : 0.0,
                                difference: hasLatest ? latest.estimated_vs_actual_label : '-',
                                timeSalaryChange: hasLatest ? `${latest.estimated_vs_actual_salary_percentage} %` : '-',
                                tolerance: hasLatest ? latest.tolerance_label : '-',
                                toleranceSalaryChange: hasLatest ? `${latest.tolerance_salary_percentage} %` : '-',
                                quality: hasLatest ? latest.welding_quality_label : '-',
                                qualitySalaryChange: hasLatest ? `${latest.welding_quality_salary_percentage} %` : '-',
                                totalPercentage: totalPercentage !== null
                                    ? `${parseFloat(totalPercentage.toFixed(1))} %`
                                    : '-',
                            });
                        }
                        return result;
                    }, []),
                }, () => {
                    this.setState({isLoading: false});
                });
            },
        ).catch((error) => {
            this.setState({isLoading: false});
            console.error(error);
        });
    };

    render = () => (
        <div>
            <h1 className="mb-5 mt-5">{gettext("Reports")}</h1>
            <div className="row mb-1">
                <div className="col-lg-4">
                    <DateRangePicker
                        setStartDay={this.setStartDay}
                        setEndDay={this.setEndDay}
                    />
                </div>
                <div className="col-lg-4 mt-3 mt-lg-0">
                    <ReportsSelect
                        name="select-welder"
                        label={gettext("Welder")}
                        selected={this.state.selectedWelder}
                        setSelected={this.setSelectedWelder}
                        endPoint={api.accountsUserList}
                        queryParams={{role: USER.ROLES.WELDER.value}}
                        callbackResultsMapFunc={user => ({
                            label: user.name,
                            value: user.id,
                        })}
                    />
                </div>
                <div className="col-lg-4">
                    <ReportsSelect
                        name="select-product"
                        label={gettext("Product")}
                        selected={this.state.selectedProduct}
                        setSelected={this.setSelectedProduct}
                        endPoint={api.productionSelectProductList}
                        callbackResultsMapFunc={product => ({
                            label: product.order.article_code,
                            value: product.id,
                        })}
                    />
                </div>
            </div>
            <div className="mb-2">
                <ReportsTable
                    selectedStartDay={this.state.selectedStartDay}
                    selectedEndDay={this.state.selectedEndDay}
                    tableRows={this.state.tableRows}
                    noDataText={this.state.noDataText}
                    isLoading={this.state.isLoading}
                />
            </div>
            <div className="mb-5">
                <ReportsTips />
            </div>
        </div>
    );
}


export default Reports;



// WEBPACK FOOTER //
// ./src/components/Reports/Reports.js